import { useState, useEffect, useRef } from 'react';
import { DateTime } from 'luxon';
import _findIndex from 'lodash.findindex';
import _find from 'lodash.find';
import { SelectButton } from 'primereact/selectbutton';
import { Button } from 'primereact/button';
import { TreeTable } from 'primereact/treetable';
import { Column } from 'primereact/column';
import { useQuery } from 'react-query';
import { Messages } from 'primereact/messages';
import { Calendar } from 'primereact/calendar';
import { getSubmittedTargets, getTargets, getTargetsTotal } from '../../services/measurement';
import { getFormattedNumberStr } from '../../utils/number';
import DivisionSummary from './divisionSummary';
import TargetTotalSummary from './targetTotalSummary';
import { useReportDetailStore } from '.';
import {
  EditableValue,
  ValueWrapper,
  TableWrapper,
  ActionsPanel,
  FilterWrapper,
  Filters,
  BtnsWrapper,
  StaticValue,
} from './common';
import EditTargetCellModal from './editTargetCellModal';
import { useSubmitTargetChanges } from '../../services/mutations';
import DiscardChangesModal from './discardChangesModal';
import UnsavedChangesNote from './unsavedChangesNote';
import ApprovalsInfo from './approvalsInfo';
import { useAuth } from '../../common/auth';
import ExportToExcelButton from '../Excel/exportToExcelButton';
import { TabName } from '../Excel/constants';
import { TargetReasonEnum } from '../../common/enum';

const categoriesData = [
  { id: 1, name: 'Electricity' },
  { id: 2, name: 'Heat' },
  { id: 9, name: 'Diesel for transportation' },
  { id: 19, name: 'HVO' },
  { id: 999, name: 'Total' },
];

const categoriesData2ndRow = [{ id: 18, name: 'Water' }];

const sourcesData = [
  { id: 'WPS', name: 'WPS' },
  { id: 'OS', name: 'OneStream' },
];

function TargetsView({ placeCode, reasonsData, tabName }: { placeCode: string; reasonsData: any[]; tabName: TabName }) {
  const [data, setData] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDiscardConfirmation, setShowDiscardConfirmation] = useState<boolean>(false);
  const [isTotalDisplayed, setIsTotalDisplayed] = useState<boolean>(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number>(1);
  const [selectedYear, setSelectedYear] = useState(DateTime.now().toJSDate());
  const [selectedSource, setSelectedSource] = useState(null);
  const messages = useRef<Messages>(null);
  const submitTargetChanges = useSubmitTargetChanges();

  const { userProfile } = useAuth();
  const { updatedCells, resultToast, setUpdatedCells, hasUnsavedData, userPermissions } = useReportDetailStore();

  const {
    data: loadedData,
    isFetching,
    error,
    refetch,
  } = useQuery(
    ['targetView', placeCode],
    () => getTargets(placeCode, selectedSource || '', selectedCategory || '', selectedYear.getFullYear()),
    { enabled: !!selectedCategory && !isTotalDisplayed && !!selectedSource },
  );

  const {
    data: totalData,
    isLoading: isTotalFetching,
    error: totalError,
    refetch: totalRefetch,
  } = useQuery(
    ['targetTotalView', placeCode],
    () => getTargetsTotal(placeCode, selectedSource || '', selectedYear.getFullYear()),
    {
      enabled: false,
    },
  );

  const {
    data: submittedData,
    isLoading: loadingSubmittedData,
    isRefetching: refetchingData,
    refetch: refetchSubmittedChanges,
  } = useQuery(
    ['submittedTargets', placeCode, selectedSource],
    () => getSubmittedTargets(placeCode, selectedSource || '', selectedYear.getFullYear()),
    {
      select: (response) => {
        if (userPermissions.canApproveTarget || userPermissions.canApproveDivisionalTarget) {
          return userPermissions.canApproveDivisionalTarget
            ? response
            : response.filter((r) => !r.approvedByFirstLevel);
        }
        return response.filter((r) => !r.targetSubmissionApproval && r.userId === userProfile.id);
      },
      onError: () => {
        resultToast.current?.show({
          severity: 'error',
          summary: 'Loading submitted targets',
          detail: 'An error has occurred while loading data.',
          life: 3000,
        });
      },
    },
  );

  const dateTimeCellTemplate = (rowData: any) =>
    rowData.data.createdAt
      ? DateTime.fromISO(rowData.data.createdAt).toLocaleString({
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric',
        })
      : '-';

  const valueCellTemplate = (rowData: any) => {
    const updatedValue = _find(
      updatedCells,
      (item: any) => item.metricId === rowData.data.metricId && item.targetType === rowData.data.targetType,
    );
    const item = updatedValue ? { ...rowData, data: { ...rowData.data, ...updatedValue } } : rowData;

    return (
      <ValueWrapper>
        {userPermissions.canSubmitTarget ? (
          <EditableValue onClick={() => setSelectedItem(item)} isUpdated={!!updatedValue}>
            <i className="pi pi-pencil" />
            {getFormattedNumberStr(Math.round(item.data.value), item.data.metricUnit)}
          </EditableValue>
        ) : (
          <StaticValue>{getFormattedNumberStr(Math.round(item.data.value), item.data.metricUnit)}</StaticValue>
        )}
      </ValueWrapper>
    );
  };

  const handleDiscard = () => {
    setShowDiscardConfirmation(false);
    setUpdatedCells([]);
    resultToast.current?.show({
      severity: 'success',
      summary: 'Discard changes',
      detail: 'Changes has been successfully discarded.',
      life: 3000,
    });
  };

  const handleUpdatedCell = (values: any) => {
    const selectedMetricId = selectedItem?.data.metricId;
    const selectedTargetType = selectedItem?.data.targetType;
    const isDivisionalTarget = selectedItem?.data.isDivisionalTarget;

    const objIdx = _findIndex(
      updatedCells,
      (item: any) => item.metricId === selectedMetricId && item.targetType === selectedTargetType,
    );

    if (objIdx !== -1) {
      const tmpUpdatedCells: any[] = updatedCells;
      const cell: any = updatedCells[objIdx];

      tmpUpdatedCells[objIdx] = {
        ...cell,
        targetType: selectedTargetType,
        isDivisionalTarget,
        value: values.value,
      };
      setUpdatedCells(tmpUpdatedCells);
    } else {
      setUpdatedCells([
        ...updatedCells,
        { metricId: selectedMetricId, ...values, targetType: selectedTargetType, isDivisionalTarget },
      ]);
    }

    setSelectedItem(null);
  };

  const handleSubmitChanges = () => {
    const proccessedUpdatedCells = {
      divisional: updatedCells.filter((i) => i.isDivisionalTarget),
      mill: updatedCells.filter((i) => !i.isDivisionalTarget),
    };

    submitTargetChanges.mutate(
      {
        code: placeCode,
        sourceName: selectedSource,
        year: selectedYear.getFullYear(),
        metricTypeId: selectedCategoryId,
        data: proccessedUpdatedCells,
      },
      {
        onSuccess: () => {
          resultToast.current?.show({
            severity: 'success',
            summary: 'Submit changes',
            detail: 'Changes has been successfully submitted for approval.',
            life: 3000,
          });
          setUpdatedCells([]);
        },
        onError: () => {
          resultToast.current?.show({
            severity: 'error',
            summary: 'Submit changes',
            detail: 'An error has occurred while submitting changes for approval.',
            life: 3000,
          });
        },
      },
    );
  };

  useEffect(() => {
    if (error) {
      messages.current?.show({ severity: 'error', summary: 'An error has occurred while loading data!', sticky: true });
    }
  }, [error]);

  useEffect(() => {
    if (loadedData) {
      setUpdatedCells([]);
      const { items, ...rest } = loadedData;
      const millData = items?.length === 1 ? items[0] : { items: [] };

      const { items: topLevelItems, ...topLevelItem } = millData;

      const processedData = {
        ...topLevelItem,
        items: topLevelItems.map((item: any) => ({
          key: item.code,
          data: {
            label: item.name,
            level: 3,
            targetType: item.targetType,
            value: item.value,
            oldValue: item.oldValue,
            metricId: item.metricId,
            metricUnit: item.metricUnit,
            createdAt: item.createdAt,
            createdBy: item.createdBy,
            reasonId: item.reasonId,
          },
          children: [
            ...item.items.map((childItem: any) => ({
              key: childItem.code,
              data: {
                label: childItem.name,
                level: 4,
                targetType: childItem.targetType,
                value: childItem.value,
                oldValue: childItem.oldValue,
                metricId: childItem.metricId,
                metricUnit: childItem.metricUnit,
                createdAt: childItem.createdAt,
                createdBy: childItem.createdBy,
                reasonId: childItem.reasonId,
              },
            })),
          ],
        })),
      };

      setData({
        ...rest,
        millData: processedData,
      });
    }
  }, [loadedData]);

  useEffect(() => {
    if (selectedSource && selectedCategory && !isTotalDisplayed) {
      refetch();
      refetchSubmittedChanges();
      setUpdatedCells([]);
    }
    if (isTotalDisplayed) {
      totalRefetch();
    }
  }, [selectedSource, selectedCategory, selectedYear, refetch]);

  useEffect(() => {
    if (categoriesData.length > 0) {
      setSelectedCategory(categoriesData[0].name);
      setSelectedCategoryId(categoriesData[0].id);
    }
  }, [categoriesData]);

  useEffect(() => {
    if (sourcesData.length > 0) {
      setSelectedSource(sourcesData[0].id);
    }
  }, [sourcesData]);

  const submittingChanges = submitTargetChanges.isLoading;

  return (
    <>
      <style>
        {`         
          .singleButton .p-button, .singleButton .p-button:first-of-type,  .singleButton .p-button:last-of-type {
          border-radius: 6px;
          }
        `}
      </style>
      <DiscardChangesModal
        isOpen={showDiscardConfirmation}
        onConfirm={handleDiscard}
        onCancel={() => setShowDiscardConfirmation(false)}
      />
      {selectedItem && (
        <EditTargetCellModal
          item={selectedItem}
          // TODO selected Wrong reporting - ask Vojtech
          defaultReason={TargetReasonEnum.WrongReporting}
          reasonsData={reasonsData}
          onSubmit={handleUpdatedCell}
          onClose={() => setSelectedItem(null)}
        />
      )}
      <Messages ref={messages} />
      <ActionsPanel>
        <FilterWrapper>
          <Filters>
            <div>
              <label htmlFor="category">Filter by category</label>
              <SelectButton
                id="category"
                disabled={isFetching || hasUnsavedData() || submittingChanges}
                value={selectedCategory}
                onChange={(e) => {
                  if (e.value != null) {
                    let selectedId: number = Number(categoriesData.find((c) => c.name === e.value)?.id);
                    setIsTotalDisplayed(selectedId == 999); // 999 represents Total category
                    setSelectedCategory(e.value);
                    setSelectedCategoryId(selectedId);
                  }
                }}
                optionValue="name"
                optionLabel="name"
                options={categoriesData}
              />
              <br />
              <SelectButton
                id="category2ndRow"
                disabled={isFetching || hasUnsavedData() || submittingChanges}
                value={selectedCategory}
                onChange={(e) => {
                  if (e.value != null) {
                    let selectedId: number = Number(categoriesData2ndRow.find((c) => c.name === e.value)?.id);
                    setIsTotalDisplayed(selectedId == 999); // 999 represents Total category
                    setSelectedCategory(e.value);
                    setSelectedCategoryId(selectedId);
                  }
                }}
                optionValue="name"
                optionLabel="name"
                options={categoriesData2ndRow}
                className="singleButton"
              />
            </div>
            <div>
              <label htmlFor="year">Filter by year</label>
              <Calendar
                id="year"
                view="year"
                dateFormat="yy"
                readOnlyInput
                keepInvalid
                value={selectedYear}
                disabled={isFetching || hasUnsavedData() || submittingChanges}
                onChange={(e) => {
                  setSelectedYear(e.value);
                }}
                hideOnDateTimeSelect
              />
            </div>
            <div>
              <label htmlFor="source">Source</label>
              <SelectButton
                id="source"
                disabled={isFetching || hasUnsavedData() || submittingChanges}
                value={selectedSource}
                onChange={(e) => {
                  if (e.value != null) {
                    setSelectedSource(e.value);
                  }
                }}
                optionValue="id"
                optionLabel="name"
                options={sourcesData}
              />
            </div>
          </Filters>
          {hasUnsavedData() && (
            <UnsavedChangesNote onDiscard={() => setShowDiscardConfirmation(true)} onSubmit={handleSubmitChanges} />
          )}
        </FilterWrapper>
        <div>
          <BtnsWrapper>
            {(userPermissions.canApproveTarget ||
              userPermissions.canApproveDivisionalTarget ||
              userPermissions.canSubmitTarget) && (
              <ApprovalsInfo
                data={submittedData}
                reasonsData={reasonsData}
                loading={loadingSubmittedData}
                refetching={refetchingData}
              />
            )}

            {userPermissions.canSubmitTarget && !isTotalDisplayed && (
              <>
                <Button
                  label="Discard changes"
                  size="small"
                  severity="warning"
                  disabled={!hasUnsavedData() || submittingChanges || isFetching}
                  onClick={() => setShowDiscardConfirmation(true)}
                />
                <Button
                  label="Save changes"
                  size="small"
                  onClick={handleSubmitChanges}
                  loading={submittingChanges}
                  disabled={!hasUnsavedData() || submittingChanges || isFetching}
                />
              </>
            )}
            <ExportToExcelButton
              placeCode={placeCode}
              tabName={tabName}
              sourceName={selectedSource || ''}
              selectedTime={selectedYear.getFullYear()}
              isDisabled={submittingChanges || isFetching}
            />
          </BtnsWrapper>
        </div>
      </ActionsPanel>

      {!isTotalDisplayed && (
        <>
          <DivisionSummary data={data} onUpdate={(item: any) => setSelectedItem(item)} />
        </>
      )}

      {isTotalDisplayed && (
        <>
          <TargetTotalSummary data={totalData} />
        </>
      )}

      <TableWrapper>
        <TreeTable
          loading={isFetching}
          rowHover
          value={data?.millData.items}
          tableStyle={{ minWidth: '50rem' }}
          style={{ display: !isTotalDisplayed ? 'block' : 'none' }}
          resizableColumns
          showGridlines
          columnResizeMode="expand"
        >
          <Column
            expander
            field="label"
            header="Target Name"
            body={(rowData) => `${rowData.data.label} (${rowData.key})`}
            style={{ width: '450px' }}
          />
          <Column field="targetType" header="Target Type" />
          <Column field="value" header="Target Value" body={valueCellTemplate} />
          <Column field="createdAt" header="Created At" body={dateTimeCellTemplate} />
          <Column
            field="createdBy"
            header="Created By"
            body={(rowData) => (rowData.data.createdBy && rowData.data.createdBy !== '' ? rowData.data.createdBy : '-')}
          />
        </TreeTable>
      </TableWrapper>
    </>
  );
}

export default TargetsView;
